
import React, { useState } from 'react';
import {
  View,
  Text,
  TextInput,
  StyleSheet,
  Alert,
  KeyboardAvoidingView,
  ScrollView,
  Platform,
} from 'react-native';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import PageHeader from '@/components/PageHeader';
import PrimaryButton from '@/components/PrimaryButton';

export default function ForgotPasswordScreen() {
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const { resetPassword } = useAuth();
  const router = useRouter();
  const insets = useSafeAreaInsets();

  const handleBackPress = () => {
    router.back();
  };

  const handleResetPassword = async () => {
    if (!email) {
      Alert.alert(t('auth.forgotPassword.errorTitle'), t('auth.forgotPassword.errorEmail'));
      return;
    }

    // PRD §5.1 — `auth_forgot_password_requested` micro event. Fires at the
    // point of intent (after basic validation, before the network call).
    import('@/utils/clarityEvents')
      .then(({ logAuthForgotPasswordRequested }) => logAuthForgotPasswordRequested())
      .catch(() => {});

    setLoading(true);

    try {
      const { error } = await resetPassword(email);

      if (error) {
        console.error('Reset password error:', error);
        
        if (error.message.toLowerCase().includes('rate limit') || 
            error.message.toLowerCase().includes('too many requests') || 
            error.message.toLowerCase().includes('email rate limit exceeded')) {
          Alert.alert(t('auth.forgotPassword.errorRateLimit'), t('auth.forgotPassword.errorRateLimitMessage'));
        } else if (error.message.toLowerCase().includes('user not found')) {
          Alert.alert(t('auth.forgotPassword.errorUserNotFound'), t('auth.forgotPassword.errorUserNotFoundMessage'));
        } else {
          Alert.alert(t('auth.forgotPassword.errorTitle'), error.message);
        }
      } else {
        Alert.alert(
          t('auth.forgotPassword.successTitle'),
          t('auth.forgotPassword.successMessage'),
          [
            {
              text: t('auth.forgotPassword.successButton'),
              onPress: () => router.back()
            }
          ]
        );
      }
    } catch (error) {
      console.error('Unexpected reset password error:', error);
      Alert.alert(t('auth.forgotPassword.errorTitle'), t('auth.forgotPassword.errorUnexpected'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      <PageHeader title={t('auth.forgotPassword.title')} onBack={handleBackPress} />

      <KeyboardAvoidingView 
        style={styles.keyboardAvoidingView}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        keyboardVerticalOffset={Platform.OS === 'ios' ? 0 : 20}
      >
        <ScrollView 
          style={styles.scrollView}
          contentContainerStyle={styles.scrollViewContent}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          {/* Content */}
          <View style={styles.section}>
            <Text style={styles.title}>
              {t('auth.forgotPassword.subtitle')}
            </Text>

            <View style={styles.field}>
              <Text style={styles.label}>{t('auth.forgotPassword.email')}</Text>
              <TextInput
                style={styles.input}
                placeholder={t('auth.forgotPassword.emailPlaceholder')}
                placeholderTextColor="#A19F97"
                color="#151515"
                value={email}
                onChangeText={setEmail}
                keyboardType="email-address"
                autoCapitalize="none"
                autoCorrect={false}
              />
            </View>
          </View>

          {/* Bottom Section */}
          <View style={[styles.bottomSection, { paddingBottom: insets.bottom + 24 }]}>
            <PrimaryButton
              title={loading ? t('auth.forgotPassword.buttonLoading') : t('auth.forgotPassword.button')}
              onPress={handleResetPassword}
              disabled={loading || !email}
            />
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  
  keyboardAvoidingView: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  scrollViewContent: {
    flexGrow: 1,
    justifyContent: 'space-between',
  },

  section: {
    flex: 1,
    paddingHorizontal: 24,
    paddingVertical: 24,
    gap: 24,
  },
  title: {
    color: '#151515',
    fontFamily: 'Sentient-Regular',
    fontSize: 24,
    lineHeight: 29,
  },
  field: {
    gap: 12,
  },
  label: {
    color: '#151515',
    fontFamily: 'Inter',
    fontWeight: '700',
    fontSize: 14,
    lineHeight: 14,
  },
  input: {
    width: '100%',
    height: 48,
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#DAD8CF',
    backgroundColor: '#FFFFFF',
    fontFamily: 'Inter',
    fontWeight: '500',
    fontSize: 14,
    color: '#151515',
    lineHeight: 20,
  },
  bottomSection: {
    paddingHorizontal: 24,
    paddingVertical: 16,
    gap: 16,
  },
});
